import qs from "query-string"
import {
  isEditing,
  durationToHours,
  filterQuestions,
  indexOfType,
  isInAlarmAgent,
} from "../common/common"
import { createAction, setterAction } from "../common/redux"
import tracker from "../common/tracker"
import * as t from "./actionTypes"
import Source from "./Source"
import * as generator from "../chatItemGenerator"
import * as searchActions from "../search/actions"
import * as simpleFlowActions from "../simpleFlow/actions"
import * as completionActions from "../completion/actions"
import * as interviewActions from "../interview/actions"
import { setPlanInformation } from "../dashboard/actions"
import {
  setCanUpdateInsurance,
  setConfiguredExperience,
  setConfiguredPlanProvider,
  setMarketplaceToken,
} from "../integration/actions"
import { takeSnapshot, restoreSnapshot, recycle } from "../redux/actions"
import { captureException } from "../common/error"
import { getBrand } from "../embed/actions"
import { messages } from "../chatItemGenerator"

/*******************************************************************************
                               Action creators
 ******************************************************************************/
export const setVisitToken = setterAction(t.SET_VISIT_TOKEN, "token")
export const addChatItem = createAction(t.ADD_CHAT_ITEM, "item")
export const updateActiveIndex = createAction(t.UPDATE_ACTIVE_INDEX, "index")
export const deleteChatItems = createAction(t.DELETE_CHAT_ITEMS, "index")
export const setLoading = createAction(t.SET_LOADING, "loading", "context")
export const setActiveImage = setterAction(t.SET_ACTIVE_IMAGE)
export const setSubmissionQueue = setterAction(t.SET_SUBMISSION_QUEUE)
export const pushSubmissionQueue = setterAction(t.ADD_TO_SUBMISSION_QUEUE)
export const setEditConfirmNeeded = setterAction(t.SET_EDIT_CONFIRM_NEEDED)
export const setEditConfirmShown = setterAction(t.SET_EDIT_CONFIRM_SHOWN)
export const setDeleteConfirmNeeded = setterAction(t.SET_DELETE_CONFIRM_NEEDED)
export const setMessage = setterAction(t.SET_MESSAGE)
export const setStashedResponse = setterAction(t.SET_STASHED_RESPONSE)
export const setInterviewStarted = setterAction(t.SET_INTERVIEW_STARTED)
export const setCachedQuestion = setterAction(t.SET_CACHED_QUESTION)
export const setResponse = createAction(t.SET_RESPONSE, "response", "index")
export const addResponse = createAction(t.ADD_RESPONSE, "response")
export const removeResponse = createAction(t.REMOVE_RESPONSE, "response")
export const setDurationAmount = createAction(
  t.SET_DURATION_AMOUNT,
  "amount",
  "index"
)
export const setDurationUnit = createAction(
  t.SET_DURATION_UNIT,
  "unit",
  "index"
)
export const setFreeText = createAction(t.SET_FREE_TEXT, "text")
export const setDiagnoses = createAction(t.SET_DIAGNOSES, "diagnoses")
export const setPredInfo = createAction(t.SET_PRED_INFO, "predInfo")
export const setResource = createAction(t.SET_RESOURCE, "resource")
export const setTrainMsgShown = setterAction(t.SET_TRAIN_MSG_SHOWN)
export const setFilterError = setterAction(t.SET_FILTER_ERROR)
export const setResponseHeight = setterAction(t.SET_RESPONSE_HEIGHT)
export const setTermModalShown = setterAction(t.SET_TERMS_MODAL_SHOWN)
export const setShowGinaModal = setterAction(t.SET_SHOW_GINA_MODAL)
export const setItemBlur = createAction(t.SET_ITEM_BLUR, "blur", "index")
export const setError = setterAction(t.SET_ERROR)
export const setErrorCode = setterAction(t.SET_ERROR_CODE)
export const setConfirmMessageShown = setterAction(t.SET_CONFIRM_MESSAGE_SHOWN)
export const setConfirmMessage = createAction(
  t.SET_CONFIRM_MESSAGE,
  "heading",
  "message",
  "submitText",
  "submitClick",
  "showCancel",
  "cancelClick"
)
const _setChatItems = setterAction(t.SET_CHAT_ITEMS)

// Max symptoms allowed in interview
const MAX_SYMPTOMS = 3

const progressMessages = [
  messages.interviewMessagesMiddle,
  messages.interviewMessagesEnd,
  "Last few questions.",
]

/*******************************************************************************
                                    Thunks
 ******************************************************************************/
export const addChatItems = items => (dispatch, getState) => {
  let { loading } = getState()
  if (!loading && items.length > 1)
    dispatch(setLoading(true, "interview/addChatItems"))

  let item = items.splice(0, 1)[0]

  if (!items.length) {
    dispatch(setLoading(false))
    dispatch(addChatItem(item))
  } else {
    dispatch(addChatItem(item))
    setTimeout(() => {
      dispatch(addChatItems(items))
    }, 1000)
  }
}

export const setChatItems = (items, animate = true) => dispatch => {
  if (animate) {
    dispatch(_setChatItems([]))
    dispatch(addChatItems(items))
  } else {
    dispatch(_setChatItems(items))
  }
}

export const addRadioResponse = response => (dispatch, getState) => {
  let { loading } = getState()
  if (loading) return

  dispatch(setResponse([response]))

  if (!response.isfreetext) {
    dispatch(answerClick())
  }
}

export const addCheckboxResponse = response => (dispatch, getState) => {
  let { chatList, loading, simpleFlow } = getState()
  if (loading) return

  if (response.token === "fakenota") return dispatch(answerClick(true))

  let activeItem = chatList.items[chatList.active]
  let selected =
    activeItem.response.filter(x => x.token === response.token).length > 0

  //If this response is being unselected, remove it from questions response list
  if (selected) return dispatch(removeResponse(response))

  //Auto submit if the response is a NOTA or skip, because they wouldn't
  // be allowed to submit any other options with it anyway
  if (response.isnota || response.isskip) {
    if (simpleFlow && simpleFlow.active && !simpleFlow.cardinalSubmitted) {
      return dispatch(simpleFlowActions.cardinalQuestionSubmit(true))
    } else {
      dispatch(setResponse([response]))
      return dispatch(answerClick())
    }
  }

  dispatch(addResponse(response))
}

export const answerClick = isFakeNota => (dispatch, getState) => {
  let { chatList, loading } = getState()
  let activeItem = chatList.items[chatList.active]
  if (loading || activeItem.response.length < 1) {
    return
  }

  let tokens = activeItem.response.map(r => r.token)

  //Check if we are editing a question and clear out log beneath
  // If they're going back and editing previous question, rolls back the state
  // If they didn't change their answer, cancel the edit
  if (isEditing(chatList.items, chatList.active)) {
    if (JSON.stringify(tokens) === JSON.stringify(chatList.stashedResponse)) {
      return dispatch(cancelEdit())
    }
    tracker.editSubmission()

    dispatch(deleteChatItems())
  }

  dispatch(setLoading(true, "interview/answerClick"))

  if (activeItem.inputType === "duration") {
    dispatch(
      addChatItem(
        generator.response(
          `${activeItem.duration.amount} ${activeItem.duration.unit}`
        )
      )
    )
  } else if (isFakeNota === true) {
    dispatch(addChatItem(generator.response(messages.preintentOptionNota)))
  } else if (activeItem.type !== "clarifier") {
    let responseText = activeItem.response
      .filter(r => !r.preselected)
      .map(r => r.freetext || r.text)
      .join(", ")
    dispatch(addChatItem(generator.response(responseText)))
  }

  // Get freetext if it exists.
  let freeText = undefined
  activeItem.response.forEach(r => {
    if (r.freetext) freeText = r.freetext
    if (r.isskip) tracker.skipQuestion()
  })
  let response = {
    answer: tokens.join(";"),
    other: durationToHours(activeItem.duration) || freeText,
    qtoken: activeItem.qtoken,
  }
  dispatch(submitAnswer(response))
  if (activeItem.type === "clarifier") {
    tracker.clarifierSubmitted()
  } else {
    tracker.answer()
  }
}

export const processQuestionResponse = data => (dispatch, getState) => {
  let { custom, chatList, search, simpleFlow } = getState()
  let { items } = chatList
  let { finished } = search

  const maxSymptomCount = simpleFlow.active
    ? simpleFlow.cardinalEvidence.options.length
    : MAX_SYMPTOMS

  dispatch(setLoading(false))

  if (data.status === 300 || data.status === 200) {
    dispatch(searchActions.addCachedSymptom())
    setTimeout(() => {
      let symptomCount = chatList.items.reduce(
        (total, item) => (item.type === "symptomBubble" ? total + 1 : total),
        0
      )
      if (custom.symptom || symptomCount === maxSymptomCount) {
        return dispatch(searchActions.startInterview())
      } else if (simpleFlow.active) {
        dispatch(simpleFlowActions.submitPresentations())
      } else {
        dispatch(searchActions.setCachedSearch(""))
        return dispatch(addChatItem(generator.symptomBubble()))
      }
    }, 1000)
  } else if (data.status === 303) {
    dispatch(completionActions.complete())
  } else if (data.results) {
    try {
      let parts = data.location.split("/")
      data.results["token"] = parts[parts.length - 1]
    } catch (e) {
      // Temporary fix for data.location being undefined
      console.log(e)
    }
    dispatch(completionActions.processResults(data.results, data.is_alarm))
  } else if (data.question) {
    let newItem = generator.interviewQuestion(data.question)

    // SEO hero bar case for pre-answered question.
    if (newItem.token === custom.question) {
      // TODO: Is this in use anymore??
      let response = {
        answer: custom.response,
      }
      return dispatch(submitAnswer(response))
    }
    if (finished) {
      newItem.filter = false
    }
    if (newItem.filter && newItem.question_context === "complaint") {
      dispatch(setCachedQuestion(newItem))
      dispatch(setLoading(true, "interview/processQuestionResponse"))
      dispatch(peekFollowup())
      // add the items to the chat
    } else {
      // if the newItem has a filter chat item will try to display it as a filter bubble
      if (newItem.filter && newItem.question_context !== "complaint") {
        newItem.filter = ""
      }

      let numOfQuestions = items.filter(
        i => i.type === "question" && i.index >= finished
      ).length

      if (numOfQuestions === 5 || numOfQuestions === 11) {
        const thisMessage = progressMessages[numOfQuestions === 5 ? 0 : 1]
        dispatch(addChatItem(generator.systemMessage(thisMessage)))
        setTimeout(() => dispatch(addChatItem(newItem)), 1000)
      } else {
        dispatch(addChatItem(newItem))
      }

      if (data.question.image && data.question.image.length) {
        tracker.media()
      }

      if (data.question.options.filter(x => x.image).length > 0) {
        tracker.pictureOptions()
      }
    }
  }
}

export const processClarifier = clar => (dispatch, getState) => {
  let { chatList, custom } = getState()
  let { items } = chatList
  items = items.filter(x => x.type === "clarifier")
  if (clar.options.length === 1) {
    let response = {
      answer: clar.options[0].token,
      qtoken: "", // Need to submit qtoken, but it's unimportant for clarifier
    }
    dispatch(setLoading(true, "interview/processClarifier"))
    dispatch(submitAnswer(response))
  } else {
    dispatch(addChatItem(generator.clarifier(clar, !items.length, custom)))
    if (clar.options.filter(x => x.image).length > 0) {
      tracker.clarifierImages()
    }
  }
}

/*
 filters need to be queued, and we can't see what the activeItem is because
 all four questions are being answered at once.
 */
export const filterResponse = response => (dispatch, getState) => {
  let { chatList } = getState()
  let filters = filterQuestions(chatList.items, chatList.active)

  // Find the question this answer belongs to and select it, we look through
  // all the active filter questions since we can't just look at activeIndex
  // since multiple filters can be shown at the same time
  let question = filters.findFirst(q => q.options.contains(response))

  dispatch(setResponse([response], question.index))

  if (!isEditing(chatList.items, chatList.active)) {
    dispatch(checkIfFiltersComplete())
  }
}

export const checkIfFiltersComplete = () => (dispatch, getState) => {
  const { chatList } = getState()
  const { items } = chatList

  const complete =
    items.filter(
      item =>
        (item.filter && item.response.length === 0) ||
        (item.inputType === "duration" &&
          (item.duration.unit === null ||
            item.duration.amount === null ||
            item.duration.amount === "" ||
            isNaN(item.duration.amount)))
    ).length === 0

  if (complete) dispatch(submitFilters())
}

export const submitFilters = () => (dispatch, getState) => {
  let { chatList, loading, queues } = getState()
  if (loading || queues.submissions.length) return

  dispatch(setFilterError(""))
  dispatch(searchActions.setFinishedInputtingSymptoms(false))
  if (isEditing(chatList.items, chatList.active)) {
    if (JSON.stringify(tokens) === JSON.stringify(chatList.stashedResponse)) {
      return dispatch(cancelEdit())
    }
    for (let i = chatList.active + 1; i < chatList.items.length; i++) {
      let item = chatList.items[i]
      if (
        item.type === "symptomBubble" &&
        item.response &&
        item.response.length
      ) {
        let filteredIndex = indexOfType(chatList.items, item)
        let deletionIndices = []
        return Source.getItems()
          .then(data => {
            let typeSeen = 0
            data.meta.forEach((x, i) => {
              if (x === "SEARCH" && typeSeen++ === filteredIndex) {
                deletionIndices.push(i)
              }
            })
            return Promise.all(deletionIndices.map(i => Source.deleteItem(i)))
          })
          .then(() => {
            tracker.editSubmission()
            dispatch(deleteChatItems())
            dispatch(submitFilters())
          })
          .catch(error => dispatch(genericError(error)))
      }
    }
    tracker.editSubmission()
    dispatch(deleteChatItems())
  }

  dispatch(setLoading(true, "interview/submitFilters"))

  let questions = filterQuestions(chatList.items, chatList.active)
  questions.forEach(q => {
    // The third filter question needs the duration tied to it's answer.
    if (q.inputType === "duration") {
      q.other = durationToHours(q.duration)
    }
    dispatch(pushSubmissionQueue(q))
  })
  queues = getState().queues
  let nextQuestion = queues.submissions.shift()
  let tokens = nextQuestion.response.map(r => r.token).join(";")
  let response = {
    answer: tokens,
    other: nextQuestion.other,
    qtoken: nextQuestion.qtoken,
  }
  dispatch(submitAnswer(response))
  tracker.submitFilter()
}

export const triggerEdit = (index, isDelete = false, shouldConfirm = true) => (
  dispatch,
  getState
) => {
  let { search, loading, chatList } = getState()
  if (loading) return

  let { editConfirm } = getState().modals
  let { items } = getState().chatList
  if (isEditing(chatList.items, chatList.active)) {
    dispatch(cancelEdit())
  }
  takeSnapshot("edit")
  if (index < search.finished)
    dispatch(searchActions.setFinishedInputtingSymptoms(false))
  if (items[index] && items[index].type === "symptomBubble") {
    dispatch(searchActions.setItemFinished(false, index))
    dispatch(searchActions.setHeader(items[index].cachedHeader || {}))

    if (!isDelete) {
      if (
        items[index + 1] &&
        (items[index + 1].filter || items[index + 1].type === "clarifier")
      ) {
        let currIndex = index + 2
        let currItem = items[currIndex]
        while (currItem && currItem.filter) {
          currItem = items[++currIndex]
        }
        dispatch(updateActiveIndex(currIndex - 1))
      } else {
        dispatch(updateActiveIndex(index))
      }
    } else {
      dispatch(searchActions.setItemText("", index))
      dispatch(updateActiveIndex(index))
    }
  } else {
    dispatch(updateActiveIndex(index))
  }

  if (isDelete) {
    if (!shouldConfirm) {
      dispatch(approveEdit(isDelete))
    } else {
      dispatch(setDeleteConfirmNeeded(true))
    }
  } else {
    if (editConfirm.shown) {
      dispatch(approveEdit(isDelete))
    } else {
      dispatch(setEditConfirmNeeded(true))
      dispatch(setEditConfirmShown(true))
    }
  }
}

export const approveEdit = isDelete => (dispatch, getState) => {
  let { chatList } = getState()
  let activeItem = chatList.items[chatList.active]
  let stashedResponse = activeItem.response
  if (stashedResponse && typeof stashedResponse !== "string") {
    stashedResponse = stashedResponse.slice(0).map(r => r.token)
  }
  if (stashedResponse) {
    dispatch(setStashedResponse(stashedResponse))
  }

  if (isDelete && activeItem.type === "symptomBubble") {
    let filteredIndex = indexOfType(chatList.items, activeItem)
    let deletionIndices = []
    Source.getItems()
      .then(data => {
        let typeSeen = 0
        data.meta.forEach((x, i) => {
          if (x === "SEARCH" && typeSeen++ === filteredIndex) {
            deletionIndices.push(i)
          }
        })
        return Promise.all(deletionIndices.map(i => Source.deleteItem(i)))
      })
      .then(() => {
        tracker.editSubmission()
        dispatch(deleteChatItems())
      })
      .catch(error => dispatch(genericError(error)))
  }

  dispatch(setEditConfirmNeeded(false))
  dispatch(setEditConfirmShown(false))
  dispatch(setDeleteConfirmNeeded(false))
}

export const durationNumericalResponse = (amount, question) => dispatch => {
  let newAmount
  if (amount === 0 || isNaN(amount)) {
    newAmount = ""
  } else if (amount > 0 && amount < 1000) {
    newAmount = amount.toString()
  }
  if (newAmount) dispatch(setDurationAmount(newAmount, question.index))
}

export const submitAnswer = response => (dispatch, getState) => {
  let {
    queues,
    editor,
    chatList,
    visit,
    feedback,
    brand,
    integration,
  } = getState()
  const { diagnosisConcern } = feedback

  Source.submitAnswer(response, visit.token)
    .then(async data => {
      // if data has a status of 303, usually comming from getting next_item
      // we need to get resume_visit to correctly continue the interview
      // this is due to submit_answer new posibility of returning a message that can't
      // be processed by next_item.
      if (data.status === 303) {
        const resumeData = await Source.resumeVisit().catch(captureException)
        dispatch(searchActions.processTutorials(resumeData))
        return
      }
      // Ensure no subsequent answers in queue can be submitted after we receive results
      if (data.results) {
        dispatch(setSubmissionQueue([]))
      }

      if (
        data.question &&
        isInAlarmAgent(chatList.items[chatList.active - 1].question_context) &&
        !isInAlarmAgent(data.question.question_context)
      ) {
        dispatch(setLoading(true))
        if (diagnosisConcern.display === "coronavirus") {
          if (brand.name === "public" || integration.configuredExperience) {
            diagnosisConcern.contentPage =
              "https://www.buoyhealth.com/diagnoses-a-z/coronavirus/"
          }
          tracker.dxocClose(diagnosisConcern.display)
          dispatch(
            interviewActions.addChatItem(
              generator.dxocClose("Coronavirus", diagnosisConcern.contentPage)
            )
          )
        }
      }

      if (!data.results && queues.submissions.length > 0) {
        let nextQuestion = queues.submissions.shift()
        let tokens = nextQuestion.response.map(r => r.token).join(";")
        let response = {
          answer: tokens,
          other: nextQuestion.other,
          qtoken: data.question.qtoken,
        }
        dispatch(submitAnswer(response))
      } else {
        dispatch(processQuestionResponse(data))
      }

      if (editor.isActive && chatList.interviewStarted) {
        dispatch(diagRanksAndPredInfo())
      }
    })
    .catch(error => {
      // This is to catch the event where the server times out while processing a
      // completed visit and doesn't get the information back to the front in time,
      // instead returning a 504, which causes the front to retry answer submission
      // on a closed visit, causing a 403.
      if (error.response && error.response.status === 403) {
        return dispatch(completionActions.complete())
      }
      dispatch(genericError(error))
    })
}

export const peekFollowup = () => (dispatch, getState) => {
  let { chatList, custom } = getState()

  Source.peekFollowup()
    .then(data => {
      dispatch(setLoading(false))

      if (!data.questions) {
        dispatch(searchActions.addCachedSymptom())
        let symptomCount = chatList.items.reduce(
          (total, item) => (item.type === "symptomBubble" ? total + 1 : total),
          0
        )
        if (custom.symptom || symptomCount === MAX_SYMPTOMS) {
          return dispatch(searchActions.startInterview())
        } else {
          dispatch(searchActions.setCachedSearch(""))
          return dispatch(addChatItem(generator.symptomBubble()))
        }
      }

      dispatch(addChatItem(chatList.cachedQuestion))
      dispatch(setCachedQuestion(null))
      data.questions.forEach(qdata => {
        qdata.filter = true

        // Handle filter questions for SEO hero bar
        if (qdata.token === custom.question) {
          qdata.preselected = true
          let newItem = generator.interviewQuestion(qdata)
          dispatch(addChatItem(newItem))
          let response = qdata.options.findFirst(
            o => o.token === custom.response
          )
          dispatch(filterResponse(response))
        } else {
          let newItem = generator.interviewQuestion(qdata)
          dispatch(addChatItem(newItem))
        }
      })

      tracker.servedFilter()
    })
    .catch(error => dispatch(genericError(error)))
}

export const diagRanksAndPredInfo = () => dispatch => {
  Source.diagRanksAndPredInfo()
    .then(({ diseases, predictor_diag_stats }) => {
      dispatch(setDiagnoses(diseases))
      dispatch(setPredInfo(predictor_diag_stats))
    })
    .catch(captureException)
}

export const genericError = (error, sentry = true) => dispatch => {
  if (error.response) {
    console.log(JSON.stringify(error.response))
  }
  // Clear out submission queue to ensure user cannot accidentally submit answers
  // from this visit when they inevitably restart after this error.
  dispatch(setSubmissionQueue([]))
  let msg = "We've encountered an error. Please try again."
  if (window.count) {
    msg = `Your computer cannot reach the buoy server. We have tried ${window.count} times to connect and have been unable to. Please try again later.`
    dispatch(setError(msg))
  } else {
    try {
      if (error.response && error.response.status === 429) {
        // Don't send crash reports for api limits exceeded, they are already in Logz.io reports.
        sentry = false
        dispatch(setErrorCode(429))
      } else if (error.response && error.response.status !== 500) {
        msg = error.response.data.msg
        if (msg) {
          dispatch(setError(msg))
        }
      } else {
        dispatch(setErrorCode(500))
      }
    } catch (e) {
      console.log(e)
    }
    if (sentry) {
      tracker.genericCrash()
      captureException(error)
    }
  }
}

export const cancelEdit = () => () => {
  restoreSnapshot("edit")
}

export const startOver = (reload = false) => () => {
  tracker.startOver()
  const oab = qs.parse(window.location.search).oab
  if (oab || reload) {
    window.location.reload()
  } else {
    recycle()
    if (window.document)
      window.document.body.scrollTop = window.document.documentElement.scrollTop = 0
  }
}

export const chooseInterviewPath = () => (dispatch, getState) => {
  const { auth, account, simpleFlow } = getState()
  if (simpleFlow && simpleFlow.diagnosis) {
    dispatch(simpleFlowActions.askCardinal())
  } else {
    dispatch(
      setChatItems(generator.initItems(account.profiles, auth.isAnonymous))
    )
  }
}

export const startPageTracker = () => () => {
  Source.startPageTracker().catch(captureException)
  tracker.startPageTracker()
}

export const exitPageTracker = () => () => {
  window.addEventListener("beforeunload", () => {
    Source.exitPageTracker().catch(captureException)
  })
}

export const processConfiguredExperience = configName => dispatch => {
  Source.configuredExperience(configName)
    .then(config => {
      if (config.marketplace_token) {
        dispatch(setConfiguredExperience(configName))
        dispatch(setMarketplaceToken(config.marketplace_token))
      }
      if (config.plan_provider) {
        dispatch(setConfiguredPlanProvider(config.plan_provider))
        dispatch(setPlanInformation({ plan_provider: config.plan_provider }))
      } else {
        dispatch(setPlanInformation(false))
      }
      if (config.can_update_insurance)
        dispatch(setCanUpdateInsurance(config.can_update_insurance))
      if (config.use_theme && config.marketplace_token) {
        dispatch(getBrand(null, config.marketplace_token))
      }
    })
    .catch(captureException)
}

export const startSectionTracker = section_title => () => {
  Source.startSectionTracker(section_title).catch(captureException)
}

export const endSectionTracker = section_title => () => {
  Source.endSectionTracker(section_title).catch(captureException)
}



// WEBPACK FOOTER //
// ./assets/interview/actions.js